// src/components/chat/WebSearchItem.tsx

import * as React from "react";
import { WebSearchToolEvent } from "../../interfaces/webToolTypes";
import WebSearchIcon from "../../../assets/WebSearch.svg";
import { isGroundingRedirect } from "../../utils/webSources";
import { SiteFavicon } from "./SiteFavicon";

interface WebSearchItemProps {
  event: WebSearchToolEvent;
  isLast: boolean;
  showConnector: boolean;
}

/**
 * Component to display a single web search event
 * Shows search query with expandable results
 */
const WebSearchItem: React.FC<WebSearchItemProps> = ({ event, isLast, showConnector }) => {
  const [isResultsExpanded, setIsResultsExpanded] = React.useState(false);

  // Get favicon URL using Google's service
  const getFaviconUrl = (url: string) => {
    try {
      const domain = new URL(url).hostname;
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=16`;
    } catch {
      return null;
    }
  };

  // Main container using flexbox columns
  const eventContainerStyle: React.CSSProperties = {
    display: "flex",
    gap: "12px",
    paddingBottom: isLast ? "0" : "16px",
  };

  // Icon column that contains the globe and connector line
  const iconColumnStyle: React.CSSProperties = {
    width: "24px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
  };

  // Icon container
  const iconStyle: React.CSSProperties = {
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2,
    fontSize: "16px",
  };

  // Connector line that spans the full height of the icon column
  const connectorLineStyle: React.CSSProperties = {
    position: "absolute",
    top: "24px", // Start after the icon
    bottom: isLast ? "0" : "-16px", // Extend to next icon
    left: "50%",
    width: "2px",
    marginLeft: "-1px",
    borderLeft: "2px dashed #D1D5DB",
    display: showConnector ? "block" : "none",
  };

  // Content column for query and results
  const contentColumnStyle: React.CSSProperties = {
    flex: 1,
    minWidth: 0, // Allow text truncation
  };

  const queryStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    marginBottom: "8px",
    cursor: "pointer",
    userSelect: "none",
  };

  const resultsStyle: React.CSSProperties = {
    fontSize: "13px",
    color: "#6B7280",
    paddingLeft: "0",
  };

  const resultItemStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    marginTop: "6px",
    cursor: "pointer",
  };

  return (
    <div style={eventContainerStyle}>
      {/* Icon column with connector */}
      <div style={iconColumnStyle}>
        <div style={iconStyle}>
          <WebSearchIcon width="16" height="16" />
        </div>
        <div style={connectorLineStyle} />
      </div>

      {/* Content column */}
      <div style={contentColumnStyle}>
        {/* Search query */}
        <div style={queryStyle} onClick={() => setIsResultsExpanded(!isResultsExpanded)}>
          <span style={{ flex: 1, fontSize: "14px" }}>{event.query}</span>
          <span style={{ fontSize: "12px", color: "#6B7280", marginRight: "8px" }}>
            {event.results.length} Results
          </span>
          <span
            style={{
              fontSize: "10px",
              transform: isResultsExpanded ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
          >
            ▼
          </span>
        </div>

        {/* Search results (collapsible) */}
        {isResultsExpanded && (
          <div style={resultsStyle}>
            {event.results.map((result, idx) => (
              <div
                key={idx}
                style={resultItemStyle}
                onClick={() => window.open(result.url, "_blank")}
              >
                {/* Gemini's results link through Google's redirect, whose host is not the site */}
                {isGroundingRedirect(result.url) ? (
                  <SiteFavicon domain={result.title} style={{ marginRight: "6px" }} />
                ) : (
                  <img
                    src={getFaviconUrl(result.url) || ""}
                    alt=""
                    style={{ width: "16px", height: "16px", marginRight: "6px" }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = "none";
                    }}
                  />
                )}
                <span
                  style={{
                    color: "#4B5563",
                    textDecoration: "underline",
                    fontSize: "13px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "300px",
                  }}
                >
                  {result.title}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WebSearchItem;
